import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import {
  CalendarWeek,
  HeavyCheck,
  HeavyClose,
  Switches,
} from '@/client/components/icons/ContinuIcons';
import { addDays, format } from 'date-fns';

import type { AssignmentFormData } from '../../types/assignmentTypes';
import AssignmentFormPreviewHeader from './AssignmentFormPreviewHeader';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SettingsFormPreviewProps {
  settings: AssignmentFormData['settings'];
  useCustomDueDate: AssignmentFormData['useCustomDueDate'];
  customDueDate: AssignmentFormData['customDueDate'];
  useDueDate: AssignmentFormData['setDueDate'];
  useAssignmentDelay: AssignmentFormData['useAssignmentDelay'];
  renderStatus?: boolean;
  isModalPreview?: boolean;
}

export default function SettingsFormPreview({
  settings,
  useCustomDueDate,
  customDueDate,
  useDueDate,
  useAssignmentDelay,
  renderStatus = false,
  isModalPreview = false,
}: SettingsFormPreviewProps) {
  const { t } = useTranslation();
  const { settingsFormVisited } = useAdminAssignmentsStore();
  const location = useLocation();
  const { pathname } = location;
  const isInstanceCreation = pathname.includes('create/instance');

  const {
    requireRecompletion,
    escalateBuddy,
    escalateManager,
    escalationContacts,
    daysToComplete,
    assignmentOptions,
    dueFromHired,
  } = settings;

  const { delay } = assignmentOptions;

  return (
    <Box>
      <AssignmentFormPreviewHeader
        formName="settings"
        icon={Switches}
        title={t('admin.assignments.settings.header')}
        renderStatus={renderStatus}
      />

      {(isInstanceCreation || settingsFormVisited) && (
        <Stack
          paddingY={isModalPreview ? 0 : 6}
          paddingX={isModalPreview ? 0 : 6}
          borderTop="1px solid"
          borderTopColor={isModalPreview ? 'transparent' : 'neutral.100'}
        >
          {daysToComplete && useDueDate && (
            <HStack marginY={4}>
              <CalendarWeek />

              <Text fontSize="14px" fontWeight={500}>
                {useCustomDueDate
                  ? t('admin.assignments.settings.preview.dueOnDate', {
                      date: format(addDays(new Date(), daysToComplete), 'MMMM do, yyyy'),
                    })
                  : dueFromHired
                  ? t('admin.assignments.settings.preview.dueInDaysFromHired', {
                      days: daysToComplete,
                    })
                  : t('admin.assignments.settings.preview.dueInDaysFromAssigned', {
                      days: daysToComplete,
                    })}
              </Text>
            </HStack>
          )}

          {useAssignmentDelay && (
            <HStack marginY={4}>
              <HeavyCheck />

              <Text fontSize="14px" fontWeight={500}>
                {t('admin.assignments.settings.preview.delayAssignment', { delay })}
              </Text>
            </HStack>
          )}

          <HStack marginY={4}>
            {requireRecompletion ? <HeavyCheck /> : <HeavyClose />}

            <Text fontSize="14px" fontWeight={500}>
              {t('admin.assignments.settings.preview.requireUniqueCompletions')}
            </Text>
          </HStack>

          <HStack marginY={4}>
            {escalateManager ? <HeavyCheck /> : <HeavyClose />}

            <Text fontSize="14px" fontWeight={500}>
              {t('admin.assignments.settings.preview.escalateToManager')}
            </Text>
          </HStack>

          <HStack marginY={4}>
            {escalateBuddy ? <HeavyCheck /> : <HeavyClose />}

            <Text fontSize="14px" fontWeight={500}>
              {t('admin.assignments.settings.preview.escalateToBuddy')}
            </Text>
          </HStack>

          <HStack marginY={4}>
            {escalationContacts && escalationContacts.length > 0 ? <HeavyCheck /> : <HeavyClose />}

            <Text fontSize="14px" fontWeight={500}>
              {t('admin.assignments.settings.preview.addEscalationContacts')}
            </Text>
          </HStack>
        </Stack>
      )}
    </Box>
  );
}
